import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, StickyNote, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

export function NoteDialog({ companyId, initialNote, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(initialNote || "");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/companies/${companyId}/note`, { report_note: note });
      toast.success("Note salvate: appariranno nel report PDF");
      setOpen(false);
      onSaved?.(note);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const generate = async (lang) => {
    setGenerating(lang);
    try {
      const { data } = await api.post(`/companies/${companyId}/ai-comment`, null, { params: { lang } });
      setNote(data.comment);
      toast.success(lang === "en"
        ? "English draft generated: review and save when ready"
        : "Commento generato: rivedilo e salva quando sei soddisfatto");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nella generazione AI");
    } finally {
      setGenerating(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setNote(initialNote || ""); }}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><StickyNote className="h-5 w-5 text-primary" /> Note e Commenti per la Banca</DialogTitle>
          <DialogDescription>Testo libero incluso nel report PDF ed Excel (sezione "Note dell'Analista"). Puoi generare una bozza con l'AI (italiano o inglese) e poi modificarla.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => generate("it")} disabled={!!generating || saving} data-testid="note-ai-generate-btn">
            {generating === "it" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2 text-primary" />}
            {generating === "it" ? "Generazione in corso..." : "Genera con AI (Italiano)"}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => generate("en")} disabled={!!generating || saving} data-testid="note-ai-generate-en-btn">
            {generating === "en" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2 text-primary" />}
            {generating === "en" ? "Generating..." : "Generate (English)"}
          </Button>
        </div>
        <Textarea
          value={note} onChange={(e) => setNote(e.target.value)} rows={10}
          placeholder="Es. L'azienda presenta una marginalità in crescita e una posizione finanziaria netta in miglioramento..."
          data-testid="note-textarea"
        />
        <DialogFooter>
          <Button onClick={save} disabled={saving || !!generating} data-testid="note-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
